import React from 'react';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import { LOCATION, ABSENCE } from './availabilityTaxonomy';

export default class AvailabilityPlannerTable extends React.Component {
  constructor(props) {
    super(props);
    // Drag-to-select a date range within a single (editable) row.
    this.state = { dragUserId: null, dragStart: null, dragEnd: null };
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }

  componentDidMount() {
    // Finalize even if the mouse is released outside the grid.
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  componentWillUnmount() {
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  startDrag(e, user, dayNumber) {
    // Left button only — a right-click opens the quick context menu and must not start a drag
    // selection (whose mouseup would also open the entry panel).
    if (e.button !== 0) {
      return;
    }
    e.preventDefault(); // suppress native text selection while dragging
    this.setState({ dragUserId: user.userId, dragStart: dayNumber, dragEnd: dayNumber });
  }

  extendDrag(user, dayNumber) {
    if (this.state.dragUserId === user.userId) {
      this.setState({ dragEnd: dayNumber });
    }
  }

  handleMouseUp() {
    const { dragUserId, dragStart, dragEnd } = this.state;
    if (dragUserId === null) {
      return;
    }
    const user = (this.props.users || []).find((u) => u.userId === dragUserId);
    this.setState({ dragUserId: null, dragStart: null, dragEnd: null });
    if (user && this.props.onCellClick) {
      this.props.onCellClick(user, Math.min(dragStart, dragEnd), Math.max(dragStart, dragEnd));
    }
  }

  isInDragRange(user, dayNumber) {
    if (this.state.dragUserId !== user.userId) {
      return false;
    }
    return (
      dayNumber >= Math.min(this.state.dragStart, this.state.dragEnd) &&
      dayNumber <= Math.max(this.state.dragStart, this.state.dragEnd)
    );
  }

  isToday(dayNumber) {
    const today = new Date();
    return (
      today.getFullYear() === this.props.year &&
      today.getMonth() === this.props.month - 1 &&
      today.getDate() === dayNumber
    );
  }

  renderTableHeader() {
    const lastDayOfMonth = dateTimeHelper.LastDayOfMonth(this.props.year, this.props.month - 1);
    const days = [];
    for (let i = 1; i <= lastDayOfMonth; i++) {
      const isWeekend = dateTimeHelper.IsWeekend(this.props.year, this.props.month - 1, i);
      days.push(
        <th
          key={i}
          className={isWeekend ? 'ap-th-weekend' : ''}
        >
          {this.isToday(i) ? <span className='ap-th-today-badge'>{i}</span> : i}
        </th>
      );
    }
    return (
      <thead>
        <tr>
          <th className='ap-th-name'>Name</th>
          {days}
        </tr>
      </thead>
    );
  }

  // Resolves the effective status of a day: only an explicit plan is shown. A day with no entry renders
  // empty (the default work location is used only to prefill the entry panel, not to display days). The
  // weekend flag and bridge marker are still returned for cell styling and the tooltip.
  resolveDay(user, dayNumber) {
    const isWeekend = dateTimeHelper.IsWeekend(this.props.year, this.props.month - 1, dayNumber);
    const dayString = dateTimeHelper.toYYYYsMMsDD(new Date(this.props.year, this.props.month - 1, dayNumber), '-');
    const plan = (user.dayPlans || []).find((p) => p.day && p.day.substring(0, 10) === dayString);

    const onDate = (list) => Array.isArray(list) && list.some((d) => d && d.substring(0, 10) === dayString);
    const movedOff = onDate(user.bridgeDaysOff); // weekday turned into a day off
    const movedWork = onDate(user.bridgeDaysWork); // weekend turned into a working day
    const bridge = movedOff ? 'off' : movedWork ? 'work' : null;

    const holidayEntry = (user.publicHolidays || []).find((h) => h.date && h.date.substring(0, 10) === dayString);
    const holiday = holidayEntry ? holidayEntry.name : null;

    if (plan) {
      const work = plan.workingHours > 0 ? LOCATION[plan.location] : null;
      const absence = plan.absenceHours > 0 ? ABSENCE[plan.absenceType] : null;
      return { plan, work, absence, isWeekend, isDefault: false, bridge, holiday };
    }
    return { plan: null, work: null, absence: null, isWeekend, isDefault: false, bridge, holiday };
  }

  renderChip(info) {
    // Part day: both a work location and an absence — split chip with icon + hours per half.
    if (info.work && info.absence) {
      return (
        <span className='ap-chip ap-split'>
          <span className={info.work.className}>
            <i className={`fa-solid ${info.work.icon}`} /> {info.plan.workingHours}
          </span>
          <span className={info.absence.className}>
            <i className={`fa-solid ${info.absence.icon}`} /> {info.plan.absenceHours}
          </span>
        </span>
      );
    }
    // Full day: a single icon chip (colour + icon carry the identity; full details in the tooltip).
    if (info.absence) {
      return (
        <span className={`ap-chip ${info.absence.className}`}>
          <i className={`fa-solid ${info.absence.icon}`} />
        </span>
      );
    }
    if (info.work) {
      const cls = info.isDefault ? `ap-chip ${info.work.className} ap-default` : `ap-chip ${info.work.className}`;
      return (
        <span className={cls}>
          <i className={`fa-solid ${info.work.icon}`} />
        </span>
      );
    }
    return null;
  }

  getTitle(user, info, dayNumber) {
    const dateStr = new Date(this.props.year, this.props.month - 1, dayNumber).toDateString().substring(0, 10);
    let title = `${user.name} — ${dateStr}`;
    if (info.work) {
      title += `\nWork: ${info.work.label}${info.plan ? ` (${info.plan.workingHours}h)` : ' (default)'}`;
    }
    if (info.absence) {
      title += `\nAbsence: ${info.absence.label} (${info.plan.absenceHours}h)`;
      if (info.plan.substitutedByForDay) {
        title += `\nBack-up on this day: ${info.plan.substitutedByForDay}`;
      }
    }
    if (!info.work && !info.absence && !info.bridge && !info.holiday) {
      title += info.isWeekend ? '\nWeekend' : '\nNo entry';
    }
    if (info.holiday) {
      title += `\nPublic holiday: ${info.holiday}`;
    }
    if (info.bridge === 'off') {
      title += '\nDay off — bridge day (a weekday moved to a weekend)';
    } else if (info.bridge === 'work') {
      title += '\nWorking day — bridge day (a weekend moved from a weekday)';
    }
    if (info.plan && info.plan.status === 'approved') {
      title += '\nApproved absence — locked (the work part can still be edited)';
    } else if (info.plan && info.plan.status === 'requested') {
      title += '\nRequested in BambooHR — awaiting approval (the work part can still be edited)';
    }
    return title;
  }

  renderCell(user, dayNumber) {
    const info = this.resolveDay(user, dayNumber);
    // BambooHR-owned days: 'approved' (confirmed) and 'requested' (pending). Both lock the absence (the
    // work part stays editable, so the day is still clickable; the panel disables just the absence fields),
    // but requested is shown as pending (clock + amber) rather than confirmed (lock).
    const approved = !!(info.plan && info.plan.status === 'approved');
    const requested = !!(info.plan && info.plan.status === 'requested');
    const editable = !this.props.readOnly && user.canEdit;
    // A public holiday (with no explicit entry) is a day off, shown with its own tint rather than the plain
    // weekend grey so it's recognisable.
    const holidayOff = !!info.holiday && !info.plan;
    // Off-styled (greyed) = a weekend that isn't a bridge working day, or a bridge day off (moved-from weekday).
    const offStyled = (info.isWeekend && info.bridge !== 'work') || info.bridge === 'off';
    // One-click quick-add on an empty editable working day: stamps a default work day without opening the
    // panel. Hidden on days off (weekends, bridge days off, public holidays) so a stray click can't stamp a
    // work day there — deliberate weekend/holiday work still goes through the panel.
    const showQuickAdd = editable && !info.plan && !offStyled && !holidayOff;
    let cellClass = editable ? 'ap-daycell ap-clickable' : 'ap-daycell';
    if (approved) cellClass += ' ap-approved';
    else if (requested) cellClass += ' ap-requested-day';
    if (holidayOff) cellClass += ' ap-holiday';
    else if (offStyled) cellClass += ' ap-weekend';
    // The today-column tint must not paint over off/holiday shading.
    if (this.isToday(dayNumber) && !offStyled && !holidayOff) cellClass += ' ap-today-col';
    if (editable && this.isInDragRange(user, dayNumber)) cellClass += ' ap-drag-range';
    // Click = single day; press-drag-release = a date range (finalized on document mouseup).
    const dragProps = editable
      ? {
          onMouseDown: (e) => this.startDrag(e, user, dayNumber),
          onMouseEnter: () => this.extendDrag(user, dayNumber),
        }
      : {};
    // Right-click quick menu (set a full work/absence day or delete) — editable, non-BambooHR-locked days
    // only. Locked days keep the browser's native menu; the tooltip already explains the lock.
    if (editable && !approved && !requested && this.props.onDayContextMenu) {
      dragProps.onContextMenu = (e) => this.props.onDayContextMenu(e, user, dayNumber, !!info.plan);
    }
    return (
      <td
        key={dayNumber}
        className={cellClass}
        data-toggle='startdateTooltip'
        data-placement='top'
        title={this.getTitle(user, info, dayNumber)}
        {...dragProps}
      >
        {approved && <i className='fa-solid fa-lock ap-approved-lock' />}
        {requested && <i className='fa-solid fa-clock ap-requested-clock' />}
        {showQuickAdd && (
          <i
            className='fa-solid fa-bolt ap-quick-add'
            title='Quick-add a default work day'
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (this.props.onQuickCreate) this.props.onQuickCreate(user, dayNumber);
            }}
          />
        )}
        {this.renderChip(info) || this.renderEmptyMarker(editable)}
      </td>
    );
  }

  // Signs an empty (no-entry) day with a chip-shaped placeholder — a faint "+" add affordance on editable
  // cells, blank on read-only ones. Bridge days are shown by swapping the cell background (see renderCell),
  // not by a chip.
  renderEmptyMarker(editable) {
    return <span className='ap-chip ap-chip-empty'>{editable && <i className='fa-solid fa-plus ap-empty-add' />}</span>;
  }

  colSpan() {
    return dateTimeHelper.LastDayOfMonth(this.props.year, this.props.month - 1) + 1;
  }

  renderNameCell(user) {
    if (this.props.readOnly) {
      // Team Grid: no department header rows — show the department under the name in the first column.
      return (
        <td className='ap-name'>
          <div className='ap-name-main'>{user.name}</div>
          {this.props.grouped && <div className='ap-name-dept'>{user.departmentName || 'Unassigned'}</div>}
        </td>
      );
    }
    return (
      <td className='ap-name'>
        <span
          onClick={() => this.props.onUserClick(user)}
          className='ap-name-link'
        >
          {user.name}
        </span>
      </td>
    );
  }

  renderUserRow(user, key, lastDayOfMonth) {
    return (
      <tr
        key={key}
        className='ap-row'
      >
        {this.renderNameCell(user)}
        {Array.from(Array(lastDayOfMonth).keys()).map((day) => this.renderCell(user, day + 1))}
      </tr>
    );
  }

  renderUsers() {
    if (!Array.isArray(this.props.users) || this.props.users.length === 0) {
      return (
        <tr>
          <td
            colSpan={this.colSpan()}
            className='ap-empty'
          >
            No users found.
          </td>
        </tr>
      );
    }
    const lastDayOfMonth = dateTimeHelper.LastDayOfMonth(this.props.year, this.props.month - 1);
    if (!this.props.grouped) {
      return this.props.users.map((user, index) => this.renderUserRow(user, `user${index}`, lastDayOfMonth));
    }

    // Team Grid: order by department, then last name, then first name (the department shows in the first
    // column per row — see renderNameCell — instead of as a separate header row).
    const sorted = [...this.props.users].sort(
      (a, b) =>
        (a.departmentName || 'Unassigned').localeCompare(b.departmentName || 'Unassigned') ||
        (a.lastName || '').localeCompare(b.lastName || '') ||
        (a.firstName || '').localeCompare(b.firstName || '') ||
        (a.name || '').localeCompare(b.name || '')
    );
    return sorted.map((user, index) => this.renderUserRow(user, `user${index}`, lastDayOfMonth));
  }

  render() {
    return (
      <div className='ap-calendar'>
        <table className='ap-table'>
          {this.renderTableHeader()}
          <tbody>{this.renderUsers()}</tbody>
        </table>
      </div>
    );
  }
}
